import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { MessageBar } from 'office-ui-fabric-react';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import clsx from 'clsx';
import { can } from '../../utils/auth';
import Link from '../Link';
import ContractActivityDetails from '../ContractActivityDetails';
import styles from './ContractActionRequired.style';

/**
 * Info module for a contract to display specific messaging to the user based on
 * the approval status of a contract, and the users role.
 *
 * @see {@link https://microsoftit.visualstudio.com/OneITVSO/_workitems/edit/5810988}
 */
const ContractActionRequired = ({
  address,
  contractId,
  agreementAddress,
  hideCta,
  className: classNameProp,
  ...other
}) => {
  const className = clsx(styles.root, classNameProp);
  const contract = useSelector(state => get(state, `entities.contracts.${address}`, {}));
  const agreement = useSelector(state => get(state, `entities.agreements.${agreementAddress}`, {}));
  const latestActivity = useSelector(state => {
    const activities = orderBy(
      Object.values(get(state, `entities.activity`, {})).filter(
        activity => activity.resource === (agreementAddress || contractId),
      ),
      ['timestamp'],
      ['desc'],
    );
    return activities.length ? activities[0] : null;
  });
  const approvalStatus = agreementAddress ? agreement.approvalStatus : contract.approvalStatus;
  // valid approval statuses in which we should show the action required module:
  const contractActionRequiredStatuses = ['PendingInitialApproval', 'Draft', 'PendingApproval'];
  const agreementActionRequiredStatuses = ['Draft', 'PendingApproval'];

  if (
    !latestActivity || // if there's no activity found, don't show this module
    !contract || // if contract not loaded yet, don't show this module
    (!agreementAddress && !contractActionRequiredStatuses.includes(contract.approvalStatus)) || // no messaging necessary for approved contracts
    (agreementAddress && !agreementActionRequiredStatuses.includes(agreement.approvalStatus)) // no messaging for agreements that aren't PendingApproval or Draft
  ) {
    return <></>;
  }

  let summary;
  let ctaLink;
  let ctaText;

  const initiatedName = can('viewActivityInitiatedBy')
    ? latestActivity?.initiatedBy?.name
    : 'Microsoft';

  switch (approvalStatus) {
    // only shows for contracts (holistic messaging when onboarding contracts & agreements together)
    case 'Draft':
      if (agreementAddress) {
        summary = latestActivity
          ? `${initiatedName} added a new royalty fee that must be submitted for approval.`
          : 'There are new contract terms and royalty fees that must be submitted for approval.';
        if (can('editAgreement')) {
          ctaLink = `/contracts/${address}/royalty-fees/${agreementAddress}/edit`;
          ctaText = 'Review and Submit';
        }
      } else {
        summary = latestActivity
          ? `${initiatedName} added new contract terms and royalty fees that must be submitted for approval.`
          : 'There are new contract terms and royalty fees that must be submitted for approval.';
        if (can('createContract')) {
          ctaLink = `/contracts/${address}/add-contract`;
          ctaText = 'Review and Submit';
        }
      }
      break;

    // only shows for contracts (holistic messaging when onboarding contracts & royalty fees together)
    case 'PendingInitialApproval':
      if (latestActivity.operation.includes('submitted')) {
        summary = latestActivity
          ? `${initiatedName} submitted contract terms and royalty fees that require approval.`
          : 'Contract terms and royalty fees have been submitted for approval.';
      }
      if (can('approveContract')) {
        ctaLink = `/contracts/${address}/approve`;
        ctaText = 'Review and Approve';
      }
      break;

    // shows for contracts OR agreements after an edit
    case 'PendingApproval':
      if (latestActivity.operation.includes('submitted')) {
        summary = `${initiatedName} made edits to ${
          agreementAddress ? 'a royalty fee' : 'contract terms'
        } that require approval. ${
          agreementAddress
            ? 'This edit may cause transactions to be reprocessed against this royalty fee.'
            : ''
        }`;
      }
      if (agreementAddress && ['submitted', 'created'].includes(latestActivity.operation)) {
        summary = `${initiatedName} added a new royalty fee that requires approval.`;
      }

      if (can('approveContract')) {
        if (agreementAddress) {
          ctaLink = `/contracts/${address}/royalty-fees/${agreementAddress}/approve-edits`;
        } else if (latestActivity?.area === 'contracts') {
          ctaLink = `/contracts/${address}/approve-edits`;
        }
        ctaText = 'Review and Approve';
      }

      break;

    default:
      break;
  }

  return !summary ? (
    <></>
  ) : (
    <MessageBar
      isMultiline
      messageBarIconProps={{ iconName: 'Error' }}
      className={className}
      {...other}
    >
      <div>
        <Text as="p" className={styles.actionRequiredTitle}>
          Action required
        </Text>
      </div>

      <Text as="p" block className={styles.actionRequiredBody}>
        {summary}
      </Text>

      {/* Only show detailed diffs for EDITS. */}
      {approvalStatus === 'PendingApproval' && (
        <>
          {latestActivity?.newData?.name && agreementAddress && (
            <Text as="p" variant="small" block className={styles.agreementName}>
              {agreement.name}
            </Text>
          )}
          <ContractActivityDetails item={latestActivity} className={styles.details} />
        </>
      )}

      {!hideCta && ctaLink && ctaText && (
        <Link href={ctaLink} className={styles.reviewButton}>
          {ctaText}
          <FontIcon
            iconName="ChevronRight"
            ariaLabel="TextDocument"
            className={styles.chevronRight}
          />
        </Link>
      )}
    </MessageBar>
  );
};

ContractActionRequired.propTypes = {
  /**
   * Contract address
   */
  address: PropTypes.string.isRequired,
  /**
   * Contract ID
   */
  contractId: PropTypes.string.isRequired,
  /**
   * Agreement address
   */
  agreementAddress: PropTypes.string,
  /**
   * Hide the call to action
   */
  hideCta: PropTypes.bool,
  className: PropTypes.string,
};

ContractActionRequired.defaultProps = {
  agreementAddress: null,
  hideCta: false,
  className: '',
};

export default ContractActionRequired;
